import { findBrigadeMember, findNpc, findTerritory, type Deed, type DeedRequire } from '@bratva/content';
import {
  COMPANION_PATIENCE_DAYS,
  bribeOutPrice,
  resolveFight,
  stageOf,
  sideStrength,
  WATCHED_HEAT_FACTOR,
  courtCompanion,
  jobXp,
  rollJob,
  sellValue,
  streamRng,
  successChance,
  type StatName,
} from '@bratva/rules';
import type { Action, SaveDoc } from '@bratva/state';
import { watchedNow } from './heat.js';
import {
  carryCapacity,
  character,
  companionSlotsFor,
  deedHeatFactor,
  deedMinutes,
  deedPayoutFactor,
  sellTerms,
  stressFor,
} from './character.js';

/**
 * Прослойка между контентом и состоянием: решает, доступно ли дело,
 * и превращает его в действие для reducer. Сами формулы живут в правилах,
 * а документ правит только reducer — здесь ни того, ни другого не дублируем.
 */

export interface DeedOption {
  deed: Deed;
  available: boolean;
  /** Почему нельзя — показывается прямо в панели, а не прячется. */
  reason?: string;
  /** Шанс успеха для дел с броском. */
  chance?: number;
  /** Сколько отдадут за хабар прямо сейчас. */
  preview?: string;
}

const STAT_TITLES: Record<StatName, string> = {
  sila: 'Сила',
  smekalochka: 'Смекалочка',
  obayanie: 'Обаяние',
  chotkost: 'Чоткость',
  avtoritet: 'Авторитет',
};

/** Статы с надетым шмотом и бонусами класса. */
function doer(save: SaveDoc) {
  return character(save).doer;
}

function missingRequirement(require: DeedRequire | undefined, save: SaveDoc): string | null {
  if (!require) return null;

  if (require.npc) {
    const rep = save.npc[require.npc.id] ?? 0;
    if (rep < require.npc.gte) {
      const who = findNpc(require.npc.id)?.title ?? require.npc.id;
      return `${who} тебя ещё не знает`;
    }
  }
  if (require.level !== undefined && save.player.level < require.level) {
    return `Нужен ${require.level}-й уровень`;
  }
  if (require.quest && save.quests[require.quest]?.state !== 'done') {
    return 'Сначала закрой дело со смотрящим';
  }

  for (const [key, need] of Object.entries(require)) {
    if (need === undefined || key === 'npc' || key === 'level' || key === 'quest') continue;
    if (typeof need !== 'number') continue;
    if (key === 'cash') {
      if (save.player.cash < need) return `Нужно ${(need / 100).toLocaleString('ru-RU')} ₽`;
      continue;
    }
    const stat = key as StatName;
    if (save.player[stat] < need) return `Нужна ${STAT_TITLES[stat]} ${need}`;
  }
  return null;
}

export function evaluate(deed: Deed, save: SaveDoc, objectId: string): DeedOption {
  const base: DeedOption = { deed, available: true };

  if (deed.once && save.world.touched.includes(`${objectId}:${deed.id}`)) {
    return { ...base, available: false, reason: 'Здесь уже всё' };
  }

  const missing = missingRequirement(deed.require, save);
  if (missing) return { ...base, available: false, reason: missing };

  switch (deed.type) {
    case 'job': {
      if (deed.loot && save.habar.length >= carryCapacity(save)) {
        const where = character(save).vehicle ? 'Багажник полон' : 'Руки заняты';
        return { ...base, available: false, reason: `${where} — сначала сбыть` };
      }
      return { ...base, chance: successChance(deed, doer(save)) };
    }
    case 'sell': {
      if (save.habar.length === 0) return { ...base, available: false, reason: 'Нечего сбывать' };
      const amount = sellAmount(save, deed.commission);
      return { ...base, preview: `${(amount / 100).toLocaleString('ru-RU')} ₽ за ${save.habar.length} шт.` };
    }
    case 'buy': {
      if (save.businesses.some((business) => business.def === deed.business.def)) {
        return { ...base, available: false, reason: 'Уже твоя' };
      }
      if (save.player.cash < deed.price) {
        return { ...base, available: false, reason: `Нужно ${(deed.price / 100).toLocaleString('ru-RU')} ₽` };
      }
      return { ...base, preview: `${(deed.business.incomePerHour / 100).toLocaleString('ru-RU')} ₽ в час за ночь` };
    }
    case 'train': {
      if (save.player.cash < deed.price) {
        return { ...base, available: false, reason: `Нужно ${(deed.price / 100).toLocaleString('ru-RU')} ₽` };
      }
      return { ...base, preview: `${STAT_TITLES[deed.stat]} +1` };
    }
    case 'court': {
      if (save.companions.length >= companionSlotsFor(save)) {
        return { ...base, available: false, reason: 'Больше не потянешь: слоты кончились' };
      }
      if (save.player.cash < deed.price) {
        return { ...base, available: false, reason: `Нужно ${(deed.price / 100).toLocaleString('ru-RU')} ₽` };
      }
      // Шанс считается той же формулой, что и дела: Обаяние против сложности.
      const chance = successChance({ difficulty: deed.difficulty, stats: { obayanie: 1 }, heat: 0, stress: 0 }, doer(save));
      return { ...base, chance, preview: 'Может выйти, а может и нет' };
    }
    case 'shop':
      return { ...base, preview: 'Костюмы, обувь, золото' };
    case 'garage':
      return { ...base, preview: 'Копейка, зубило, бумер' };
    case 'talk': {
      const rep = save.npc[deed.npc] ?? 0;
      return { ...base, preview: `Репутация ${rep >= 0 ? '+' : ''}${rep} → +${deed.reputation}` };
    }
    case 'gift': {
      if (save.player.cash < deed.price) {
        return { ...base, available: false, reason: `Нужно ${(deed.price / 100).toLocaleString('ru-RU')} ₽` };
      }
      return { ...base, preview: `+${deed.reputation} репутации за ${(deed.price / 100).toLocaleString('ru-RU')} ₽` };
    }
    case 'bribe': {
      if (save.player.heat === 0) return { ...base, available: false, reason: 'Пока не за что' };
      if (save.player.cash < deed.price) {
        return { ...base, available: false, reason: `Нужно ${(deed.price / 100).toLocaleString('ru-RU')} ₽` };
      }
      return { ...base, preview: `Палево −${deed.heatRelief}` };
    }
    case 'takeover': {
      const territory = findTerritory(deed.territory);
      if (!territory) return { ...base, available: false, reason: 'Территории нет' };
      if (save.territories.some((held) => held.id === territory.id)) {
        return { ...base, available: false, reason: 'Уже твоя' };
      }
      if (save.player.health < 40) return { ...base, available: false, reason: 'Сначала подлечись' };
      return {
        ...base,
        chance: fightOdds(save, territory.enemy),
        preview: `${territory.holder} · доля +${territory.incomeBonus}% · держать от ${territory.holdAvtoritet} авторитета`,
      };
    }
    case 'hire': {
      const member = findBrigadeMember(deed.member);
      if (!member) return { ...base, available: false, reason: 'Такого не знают' };
      if (save.brigade.some((slot) => slot.member === member.member)) {
        return { ...base, available: false, reason: 'Уже с тобой' };
      }
      if (save.player.cash < member.price) {
        return { ...base, available: false, reason: `Нужно ${(member.price / 100).toLocaleString('ru-RU')} ₽` };
      }
      return { ...base, preview: `Сила +${member.sila} в драке · доля ${member.share}% с точек` };
    }
    case 'settle': {
      const stage = stageOf(save.justice, save.day);
      if (stage === 'clean') return { ...base, available: false, reason: 'Пока не за что' };
      const price = bribeOutPrice(save.justice, save.npc['smotryashiy'] ?? 0);
      if (save.player.cash < price) {
        return { ...base, available: false, reason: `Нужно ${(price / 100).toLocaleString('ru-RU')} ₽` };
      }
      return { ...base, preview: `${(price / 100).toLocaleString('ru-RU')} ₽ — и следствия нет` };
    }
    case 'fight': {
      if (save.player.health < 30) return { ...base, available: false, reason: 'Здоровья не хватит' };
      return {
        ...base,
        chance: fightOdds(save, deed.enemy),
        preview: `${deed.enemy.name}: ${deed.enemy.count} шт.`,
      };
    }
    case 'shop':
      return { ...base, preview: 'Костюмы, обувь, золото' };
    case 'garage':
      return { ...base, preview: 'Копейка, зубило, бумер' };
    case 'talk': {
      const rep = save.npc[deed.npc] ?? 0;
      return { ...base, preview: `Репутация ${rep >= 0 ? '+' : ''}${rep} → +${deed.reputation}` };
    }
    case 'gift': {
      if (save.player.cash < deed.price) {
        return { ...base, available: false, reason: `Нужно ${(deed.price / 100).toLocaleString('ru-RU')} ₽` };
      }
      return { ...base, preview: `+${deed.reputation} репутации за ${(deed.price / 100).toLocaleString('ru-RU')} ₽` };
    }
    case 'bribe': {
      if (save.player.heat === 0) return { ...base, available: false, reason: 'Пока не за что' };
      if (save.player.cash < deed.price) {
        return { ...base, available: false, reason: `Нужно ${(deed.price / 100).toLocaleString('ru-RU')} ₽` };
      }
      return { ...base, preview: `Палево −${deed.heatRelief}` };
    }
    case 'takeover': {
      const territory = findTerritory(deed.territory);
      if (!territory) return { ...base, available: false, reason: 'Территории нет' };
      if (save.territories.some((held) => held.id === territory.id)) {
        return { ...base, available: false, reason: 'Уже твоя' };
      }
      if (save.player.health < 40) return { ...base, available: false, reason: 'Сначала подлечись' };
      return {
        ...base,
        chance: fightOdds(save, territory.enemy),
        preview: `${territory.holder} · доля +${territory.incomeBonus}% · держать от ${territory.holdAvtoritet} авторитета`,
      };
    }
    case 'hire': {
      const member = findBrigadeMember(deed.member);
      if (!member) return { ...base, available: false, reason: 'Такого не знают' };
      if (save.brigade.some((slot) => slot.member === member.member)) {
        return { ...base, available: false, reason: 'Уже с тобой' };
      }
      if (save.player.cash < member.price) {
        return { ...base, available: false, reason: `Нужно ${(member.price / 100).toLocaleString('ru-RU')} ₽` };
      }
      return { ...base, preview: `Сила +${member.sila} в драке · доля ${member.share}% с точек` };
    }
    case 'settle': {
      const stage = stageOf(save.justice, save.day);
      if (stage === 'clean') return { ...base, available: false, reason: 'Пока не за что' };
      const price = bribeOutPrice(save.justice, save.npc['smotryashiy'] ?? 0);
      if (save.player.cash < price) {
        return { ...base, available: false, reason: `Нужно ${(price / 100).toLocaleString('ru-RU')} ₽` };
      }
      return { ...base, preview: `${(price / 100).toLocaleString('ru-RU')} ₽ — и следствия нет` };
    }
    case 'fight': {
      if (save.player.health < 30) return { ...base, available: false, reason: 'Здоровья не хватит' };
      return {
        ...base,
        chance: fightOdds(save, deed.enemy),
        preview: `${deed.enemy.name}: ${deed.enemy.count} шт.`,
      };
    }
    case 'date': {
      const companion = save.companions[0];
      if (!companion) return { ...base, available: false, reason: 'Не с кем гулять' };
      if (save.player.cash < companion.giftPrice) {
        return { ...base, available: false, reason: `Подарок стоит ${(companion.giftPrice / 100).toLocaleString('ru-RU')} ₽` };
      }
      const patience = COMPANION_PATIENCE_DAYS - (save.day - companion.lastDateDay);
      const warn = patience <= 1 ? ' · пора, иначе уйдёт' : '';
      return { ...base, preview: `${companion.title}: стресс −${companion.stressRelief}${warn}` };
    }
    case 'rest': {
      const nothingToFix =
        save.player.stress === 0 && save.player.health === 100 && (deed.heatRelief === 0 || save.player.heat === 0);
      if (nothingToFix) return { ...base, available: false, reason: 'Сыт, спокоен и чист' };
      if (save.player.cash < deed.price) {
        return { ...base, available: false, reason: `Нужно ${(deed.price / 100).toLocaleString('ru-RU')} ₽` };
      }
      const parts = [
        deed.stressRelief > 0 ? `стресс −${deed.stressRelief}` : null,
        deed.heatRelief > 0 ? `палево −${deed.heatRelief}` : null,
      ].filter(Boolean);
      return { ...base, preview: parts.join(', ') };
    }
    default: {
      const never: never = deed;
      return never;
    }
  }
}

/** Наша сторона в драке: пацан и вся бригада. */
function ourSide(save: SaveDoc) {
  const { doer } = character(save);
  return [
    { name: 'ты', sila: doer.sila, chotkost: doer.chotkost, health: save.player.health },
    ...save.brigade.map((slot) => ({
      name: slot.title,
      sila: slot.sila,
      chotkost: slot.chotkost,
      health: slot.health,
    })),
  ];
}

interface EnemySpec {
  name: string;
  sila: number;
  chotkost: number;
  health: number;
  count: number;
  weapon?: number;
}

function enemySide(enemy: EnemySpec) {
  return Array.from({ length: enemy.count }, (_, index) => ({
    name: enemy.count > 1 ? `${enemy.name} ${index + 1}` : enemy.name,
    sila: enemy.sila,
    chotkost: enemy.chotkost,
    health: enemy.health,
    weapon: enemy.weapon,
  }));
}

/** Прикидка шансов для панели: по силе сторон, без броска. */
function fightOdds(save: SaveDoc, enemy: EnemySpec): number {
  const ours = sideStrength(ourSide(save));
  const theirs = (enemy.sila + enemy.chotkost * 0.3 + (enemy.weapon ?? 0)) * enemy.count;
  return ours / (ours + theirs);
}

/** Сколько отдадут за хабар: комиссия и цена зависят от сетов. */
export function sellAmount(save: SaveDoc, commission: number): number {
  const terms = sellTerms(save, commission);
  return Math.round(sellValue(save.habar, terms.commission) * terms.priceFactor);
}

/** Превращает выбранное дело в действие для reducer. */
export function perform(deed: Deed, save: SaveDoc, objectId: string): Action {
  const touch = deed.once ? `${objectId}:${deed.id}` : undefined;

  switch (deed.type) {
    case 'job': {
      // Бросок детерминирован сидом, днём и минутой: повтор того же клика
      // не даёт переролла, а баг воспроизводится по сейву.
      const rng = streamRng(save.seed, `deed:${deed.id}:${objectId}:${save.minute}`, save.day);
      const rolled = rollJob(deed, doer(save), rng);

      // Класс, шмот и патруль правят результат: выплата, палево и стресс.
      const seen = deed.heat > 0 && watchedNow(save) !== null;
      const heatFactor = deedHeatFactor(save, deed) * (seen ? WATCHED_HEAT_FACTOR : 1);
      const payoutFactor = deedPayoutFactor(save, deed);
      const outcome = {
        ...rolled,
        payout: Math.round(rolled.payout * payoutFactor),
        lootValue: Math.round(rolled.lootValue * payoutFactor),
        heat: Math.round(rolled.heat * heatFactor),
        stress: stressFor(save, rolled.stress),
      };

      return {
        type: 'job',
        title: deed.title,
        minutes: deedMinutes(save, deed),
        outcome,
        xp: outcome.success ? jobXp({ difficulty: deed.difficulty, payout: deed.payout, lootValue: deed.loot?.value }) : 0,
        loot:
          deed.loot && outcome.success
            ? { def: deed.loot.def, title: deed.loot.title, value: outcome.lootValue }
            : undefined,
        touch: outcome.success ? touch : undefined,
      };
    }
    case 'sell':
      return {
        type: 'sell',
        minutes: deedMinutes(save, deed),
        amount: sellAmount(save, deed.commission),
        count: save.habar.length,
        heat: deed.heat,
      };
    case 'buy':
      return {
        type: 'buy',
        minutes: deed.minutes,
        price: deed.price,
        stress: deed.stress,
        business: {
          def: deed.business.def,
          title: deed.business.title,
          zone: save.player.zone,
          level: 1,
          incomePerHour: deed.business.incomePerHour,
          heatPerNight: deed.business.heatPerNight,
        },
      };
    case 'train':
      return { type: 'train', minutes: deed.minutes, price: deed.price, stat: deed.stat, stress: deed.stress };
    case 'court': {
      const rng = streamRng(save.seed, `court:${objectId}:${save.minute}`, save.day);
      const tier = courtCompanion(rng, save.player.obayanie);
      return {
        type: 'court',
        title: deed.title,
        minutes: deed.minutes,
        price: deed.price,
        stress: deed.stress,
        companion: tier
          ? {
              def: tier.def,
              title: tier.title,
              tier: tier.tier,
              stressRelief: tier.stressRelief,
              giftPrice: tier.giftPrice,
              lastDateDay: save.day,
            }
          : undefined,
        touch,
      };
    }
    case 'date': {
      const companion = save.companions[0];
      if (!companion) throw new Error('прогулка без спутницы: панель не должна была это предлагать');
      return { type: 'date', minutes: deed.minutes, companion, touch };
    }
    case 'shop':
      // Магазин сам ничего не меняет: время уходит на разговоры с барыгой,
      // покупки — отдельными действиями из панели.
      return { type: 'spendMinutes', minutes: deed.minutes, text: 'Пошёл смотреть шмот.' };
    case 'garage':
      return { type: 'spendMinutes', minutes: deed.minutes, text: 'Пошёл смотреть тачки.' };
    case 'talk':
      return {
        type: 'talk',
        title: deed.title,
        npc: deed.npc,
        minutes: deed.minutes,
        reputation: deed.reputation,
        touch,
      };
    case 'gift':
      return {
        type: 'gift',
        title: deed.title,
        npc: deed.npc,
        minutes: deed.minutes,
        price: deed.price,
        reputation: deed.reputation,
        touch,
      };
    case 'bribe':
      return {
        type: 'bribe',
        title: deed.title,
        minutes: deed.minutes,
        price: deed.price,
        heatRelief: deed.heatRelief,
      };
    case 'takeover': {
      const territory = findTerritory(deed.territory);
      if (!territory) throw new Error(`нет территории ${deed.territory}`);
      const rng = streamRng(save.seed, `takeover:${deed.id}:${save.minute}`, save.day);
      const result = resolveFight(ourSide(save), enemySide(territory.enemy), rng);
      return {
        type: 'takeover',
        title: deed.title,
        territoryId: territory.id,
        territoryTitle: territory.title,
        minutes: deedMinutes(save, deed),
        won: result.won,
        damage: result.damageTaken,
        avtoritet: Math.max(5, Math.round(territory.holdAvtoritet / 3)),
        heat: Math.round(deed.heat * deedHeatFactor(save, deed)),
        stress: stressFor(save, deed.stress),
        log: result.rounds.map((round) => round.text),
      };
    }
    case 'settle':
      return {
        type: 'settleCourt',
        price: bribeOutPrice(save.justice, save.npc['smotryashiy'] ?? 0),
        how: 'Смотрящий подключил кого надо',
      };
    case 'hire': {
      const member = findBrigadeMember(deed.member);
      if (!member) throw new Error(`нет пацана ${deed.member}`);
      return {
        type: 'hire',
        title: deed.title,
        minutes: deed.minutes,
        price: member.price,
        slot: {
          member: member.member,
          title: member.title,
          sila: member.sila,
          chotkost: member.chotkost,
          health: member.health,
          share: member.share,
        },
      };
    }
    case 'fight': {
      const rng = streamRng(save.seed, `fight:${deed.id}:${objectId}:${save.minute}`, save.day);
      const result = resolveFight(ourSide(save), enemySide(deed.enemy), rng);
      return {
        type: 'fight',
        title: deed.title,
        minutes: deedMinutes(save, deed),
        won: result.won,
        damage: result.damageTaken,
        payout: result.won ? Math.round(deed.payout * deedPayoutFactor(save, deed)) : 0,
        avtoritet: deed.avtoritet,
        heat: Math.round(deed.heat * deedHeatFactor(save, deed)),
        stress: stressFor(save, deed.stress),
        log: result.rounds.map((round) => round.text),
        touch: result.won ? touch : undefined,
      };
    }
    case 'rest':
      return {
        type: 'rest',
        title: deed.title,
        minutes: deed.minutes,
        stressRelief: deed.stressRelief,
        healthRelief: deed.healthRelief,
        heatRelief: deed.heatRelief,
        price: deed.price,
        touch,
      };
    default: {
      const never: never = deed;
      return never;
    }
  }
}
